import type { GridSelection, Item } from "@glideapps/glide-data-grid";
import {
  AI_SCRIPT_CHAT_CONTEXT_KIND,
  type AiScriptChatSelectionContext,
  type IAiSpreadsheetCell,
  type IAiSpreadsheetLoadedSheet,
} from "@/payroll/ai-script/types";
import type { AdaptedSpreadsheetSheet } from "./adapter";
import { emptySelection, formatColumnLetter, formatCoordinate } from "./utils";

const MAX_CONTEXT_ENTRIES = 40;

const getCellDisplayValue = (cell: IAiSpreadsheetCell | undefined) => {
  if (!cell) return null;
  if (cell.computed_value != null) return String(cell.computed_value);

  return cell.raw_input ?? null;
};

export const buildAiScriptChatSelectionContext = ({
  adaptedSheet,
  activeSheet,
  gridSelection,
  getSavedRow,
}: {
  adaptedSheet: AdaptedSpreadsheetSheet;
  activeSheet: IAiSpreadsheetLoadedSheet;
  gridSelection: GridSelection;
  getSavedRow: (displayRow: number) => number;
}): AiScriptChatSelectionContext | null => {
  const selectedRow = gridSelection.rows.first();
  const selectedColumn = gridSelection.columns.first();
  const selectedCell = gridSelection.current?.cell ?? null;

  if (selectedRow !== undefined) {
    const cells = adaptedSheet.columns
      .map((_, colIndex) => ({
        header: getCellDisplayValue(adaptedSheet.getCellRecord(colIndex, 0)),
        value: getCellDisplayValue(
          adaptedSheet.getCellRecord(colIndex, selectedRow),
        ),
      }))
      .filter(
        (entry): entry is { header: string | null; value: string } =>
          entry.value != null,
      )
      .slice(0, MAX_CONTEXT_ENTRIES);

    return {
      kind: AI_SCRIPT_CHAT_CONTEXT_KIND.row,
      sheetName: activeSheet.name,
      rowNumber: getSavedRow(selectedRow) + 1,
      rowLabel: getCellDisplayValue(adaptedSheet.getCellRecord(0, selectedRow)),
      cells,
    };
  }

  if (selectedColumn !== undefined) {
    const allValues: { rowLabel: string | null; value: string }[] = [];
    for (let row = 1; row < adaptedSheet.rowCount; row += 1) {
      const value = getCellDisplayValue(
        adaptedSheet.getCellRecord(selectedColumn, row),
      );
      if (value == null) continue;
      allValues.push({
        rowLabel: getCellDisplayValue(adaptedSheet.getCellRecord(0, row)),
        value,
      });
    }

    return {
      kind: AI_SCRIPT_CHAT_CONTEXT_KIND.column,
      sheetName: activeSheet.name,
      columnLetter: formatColumnLetter(selectedColumn),
      columnHeader: getCellDisplayValue(
        adaptedSheet.getCellRecord(selectedColumn, 0),
      ),
      values: allValues.slice(0, MAX_CONTEXT_ENTRIES),
      isTruncated: allValues.length > MAX_CONTEXT_ENTRIES,
    };
  }

  if (!selectedCell) return null;

  const savedRow = getSavedRow(selectedCell[1]) + 1;
  const savedCol = selectedCell[0] + 1;
  const region = activeSheet.regions?.find(
    (candidate) =>
      candidate.validation.valid &&
      savedRow >= candidate.header_row &&
      savedRow <= candidate.record_end_row &&
      savedCol >= candidate.start_col &&
      savedCol <= candidate.end_col,
  );
  const headerCell = region
    ? activeSheet.cells.find(
        (cell) => cell.row === region.header_row && cell.col === savedCol,
      )
    : adaptedSheet.getCellRecord(selectedCell[0], 0);

  return {
    kind: AI_SCRIPT_CHAT_CONTEXT_KIND.cell,
    sheetName: activeSheet.name,
    coordinate: formatCoordinate([
      selectedCell[0],
      getSavedRow(selectedCell[1]),
    ]),
    value: getCellDisplayValue(
      adaptedSheet.getCellRecord(selectedCell[0], selectedCell[1]),
    ),
    columnHeader: getCellDisplayValue(headerCell),
    rowLabel: getCellDisplayValue(
      adaptedSheet.getCellRecord(0, selectedCell[1]),
    ),
  };
};

export const buildAiScriptChatCellQuestion = ({
  adaptedSheet,
  activeSheet,
  cell,
  getSavedRow,
}: {
  adaptedSheet: AdaptedSpreadsheetSheet;
  activeSheet: IAiSpreadsheetLoadedSheet;
  cell: Item;
  getSavedRow: (displayRow: number) => number;
}) => {
  const [col, row] = cell;
  const selection: GridSelection = {
    ...emptySelection,
    current: {
      cell,
      range: { x: col, y: row, width: 1, height: 1 },
      rangeStack: [],
    },
  };
  const context = buildAiScriptChatSelectionContext({
    adaptedSheet,
    activeSheet,
    gridSelection: selection,
    getSavedRow,
  });
  return context?.kind === AI_SCRIPT_CHAT_CONTEXT_KIND.cell &&
    context.value?.trim()
    ? { selection, context }
    : null;
};
